import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  Avatar,
  StepConnector
} from '@mui/material';
import { Google as GoogleIcon, FamilyRestroom, CloudUpload, CheckCircleOutline } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { ref, uploadBytes } from 'firebase/storage';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, storage, db } from '../firebase';
import { familyCodeService } from '../services/familyCodeService';
import { invitationService } from '../services/invitationService';
import { userProfileService } from '../services/userProfileService';
import { accountDeletionService } from '../services/accountDeletionService';
import { subscriptionService } from '../services/subscriptionService';
import ExistingAccountDialog from './ExistingAccountDialog';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';
import { isInviteOnlyMode } from '../utils/inviteOnlyMode';
import { userDocHasFamilyContext } from '../utils/familyScope';

// Simplified step structure matching actual flow
const STEPS = [
  'Account Type',
  'Basic Info',
  'Family Info',
  'Profile Picture',
  'Complete'
];

const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse/Partner' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'sibling-child', label: "Sibling's Child (Niece/Nephew)" },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'grandchild', label: 'Grandchild' },
  { value: 'aunt/uncle', label: 'Aunt/Uncle' },
  { value: 'cousin', label: 'Cousin' },
  { value: 'friend', label: 'Friend' },
  { value: 'caregiver', label: 'Caregiver' },
  { value: 'power-of-attorney', label: 'Power of Attorney' },
  { value: 'executor', label: 'Executor' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'attorney', label: 'Attorney' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'financial-planner', label: 'Financial Planner' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'emergency-contact', label: 'Emergency Contact' },
  { value: 'other', label: 'Other' }
];

const AccountCreationWizard = () => {
  const navigate = useNavigate();
  const { refreshUserData } = useAuth();
  const { familyCode, inviteCode } = useParams(); // Get familyCode and inviteCode from URL (/join/:familyCode/:inviteCode)
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(!!inviteCode);
  const [finalized, setFinalized] = useState(false);

  // Smart Join Flow: Existing account detection
  const [existingAccountDialogOpen, setExistingAccountDialogOpen] = useState(false);
  const [existingUserData, setExistingUserData] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Get family code from URL if invited (legacy support)
  const searchParams = new URLSearchParams(window.location.search);
  const familyCodeFromUrl = searchParams.get('familyCode');

  const [formData, setFormData] = useState({
    // Basic info
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',

    // Family info
    isNewFamily: familyCodeFromUrl || inviteCode ? false : null,
    familyCode: familyCodeFromUrl || '',
    familyName: '',
    relationship: 'other',

    // Profile
    profilePicture: null,

    // Legal
    termsAccepted: false,

    // Internal
    userId: null,
    ssoProvider: null, // 'google' or 'microsoft'
    ssoUser: null
  });

  // Handle case where user is already authenticated when wizard mounts.
  // If the user already has a completed Firestore profile (familyCode),
  // redirect them to home — they don't need the signup wizard.
  // Otherwise, pre-populate formData from their SSO profile.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user || formData.userId) return;

    const checkAndSetup = async () => {
      // Check if this user already has a completed profile in Firestore
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (userDocHasFamilyContext(data)) {
            // Existing user — if they arrived via an invite link, send them
            // back to the invite URL so InviteAcceptPage can handle it.
            // Otherwise redirect home.
            navigate(inviteCode && familyCode ? `/join/${familyCode}/${inviteCode}` : '/');
            return;
          }
        }
      } catch (err) {
        logger.error('Error checking existing user profile in wizard:', err);
      }

      // New user or incomplete profile — set up formData for wizard
      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');
      const providerId = user.providerData?.[0]?.providerId;

      const ssoType = providerId === 'google.com' ? 'google'
                    : providerId === 'microsoft.com' ? 'microsoft'
                    : providerId ? 'existing'
                    : null;

      if (ssoType) {
        setFormData(prev => ({
          ...prev,
          ssoProvider: ssoType,
          ssoUser: user,
          email: user.email || prev.email,
          firstName: prev.firstName || nameParts[0] || '',
          lastName: prev.lastName || nameParts.slice(1).join(' ') || '',
          userId: user.uid
        }));
      }
    };

    checkAndSetup();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load invitation details if invite code in URL
  useEffect(() => {
    const loadInvitation = async () => {
      if (!inviteCode || !familyCode) {
        setLoadingInvitation(false);
        return;
      }

      try {
        setLoadingInvitation(true);
        // Pass familyCode to directly access the invitation
        const inviteData = await invitationService.getInvitation(inviteCode, familyCode);

        setInvitation(inviteData);
        setFormData(prev => ({
          ...prev,
          isNewFamily: false,
          familyCode: inviteData.familyCode,
          relationship: inviteData.relationship || 'other',
          email: inviteData.inviteeEmail || '',
          phone: inviteData.inviteePhone || '',
          firstName: inviteData.inviteeName?.split(' ')[0] || '',
          lastName: inviteData.inviteeName?.split(' ').slice(1).join(' ') || ''
        }));

        // Skip to basic info step for invited users
        setActiveStep(1);
      } catch (error) {
        logger.error('Error loading invitation:', error);
        setError(error.message);
      } finally {
        setLoadingInvitation(false);
      }
    };

    loadInvitation();
  }, [inviteCode, familyCode]);

  // Auto-advance if invited via URL (legacy)
  useEffect(() => {
    if (familyCodeFromUrl && !inviteCode && activeStep === 0) {
      setActiveStep(1); // Skip account type selection
    }
  }, [familyCodeFromUrl, inviteCode, activeStep]);

  // Auto-finalize when reaching the Complete step.
  // Guard on !error so a failed attempt shows the error rather than
  // immediately retrying — the user must click "Try Again" explicitly.
  useEffect(() => {
    if (activeStep === 4 && !loading && !finalized && !error) {
      // Only finalize once when reaching the Complete step
      finalizeAccount();
    }
  }, [activeStep, finalized, error]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0: // Account Type
        // In invite-only mode, isNewFamily is forced to false (joining)
        if (isInviteOnlyMode()) return true;
        return formData.isNewFamily !== null;

      case 1: // Basic Info
        if (!formData.termsAccepted) return false;
        // If SSO, only need name
        if (formData.ssoProvider) {
          return formData.firstName && formData.lastName;
        }
        // Email/password signup
        return formData.email &&
               formData.password &&
               formData.firstName &&
               formData.lastName;

      case 2: // Family Info
        if (formData.isNewFamily) {
          return formData.familyName?.trim();
        } else {
          // SECURITY: Must have valid invitation to join
          if (!invitation && !inviteCode && !formData.familyCode) {
            return false; // Block if no invitation
          }
          return formData.relationship; // Just need relationship if invited
        }

      case 3: // Profile Picture (optional)
        return true;

      default:
        return true;
    }
  };

  const handleGoogleSSO = async () => {
    try {
      setLoading(true);
      setError(null);

      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if this Google account already has a completed profile
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (userDocHasFamilyContext(data)) {
          // Existing user — if they arrived via an invite link send them back
          // to it so InviteAcceptPage handles the join; otherwise go home.
          await refreshUserData();
          navigate(inviteCode && familyCode ? `/join/${familyCode}/${inviteCode}` : '/');
          return;
        }
      }

      // New user — extract name from Google profile and continue wizard
      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');

      setFormData(prev => ({
        ...prev,
        ssoProvider: 'google',
        ssoUser: user,
        email: user.email || '',
        firstName: prev.firstName || nameParts[0] || '',
        lastName: prev.lastName || nameParts.slice(1).join(' ') || '',
        userId: user.uid
      }));

      // Auto-advance to next step
      setActiveStep(prev => prev + 1);
    } catch (error) {
      logger.error('Google SSO error:', error);
      setError(error.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSSO = async () => {
    try {
      setLoading(true);
      setError(null);

      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        prompt: 'select_account',
        tenant: 'common' // Allows personal and work accounts
      });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if this Microsoft account already has a completed profile
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (userDocHasFamilyContext(data)) {
          // Existing user — if they arrived via an invite link send them back
          // to it so InviteAcceptPage handles the join; otherwise go home.
          await refreshUserData();
          navigate(inviteCode && familyCode ? `/join/${familyCode}/${inviteCode}` : '/');
          return;
        }
      }

      // New user — extract name from Microsoft profile and continue wizard
      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');

      setFormData(prev => ({
        ...prev,
        ssoProvider: 'microsoft',
        ssoUser: user,
        email: user.email || '',
        firstName: prev.firstName || nameParts[0] || '',
        lastName: prev.lastName || nameParts.slice(1).join(' ') || '',
        userId: user.uid
      }));

      // Auto-advance to next step
      setActiveStep(prev => prev + 1);
    } catch (error) {
      logger.error('Microsoft SSO error:', error);
      setError(error.message || 'Failed to sign in with Microsoft');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Smart Join Flow: Check if email already exists
   */
  const checkExistingEmail = async (email) => {
    if (!email) return { exists: false };

    setCheckingEmail(true);
    try {
      const result = await userProfileService.checkEmailExists(email);
      return result;
    } catch (err) {
      logger.error('Error checking email:', err);
      return { exists: false };
    } finally {
      setCheckingEmail(false);
    }
  };

  /**
   * Smart Join Flow: Handle merge - sign in and join new family
   */
  const handleMergeAccount = async (password) => {
    try {
      setLoading(true);
      setError(null);

      // Sign in with existing credentials
      const userCredential = await signInWithEmailAndPassword(
        auth,
        existingUserData.email,
        password
      );

      const userId = userCredential.user.uid;

      // If user is in a different family, leave it first
      if (existingUserData.familyCode && existingUserData.familyCode !== invitation?.familyCode) {
        await familyCodeService.leaveFamily(userId, existingUserData.familyCode);
      }

      // Accept the invitation and join the new family
      if (invitation || inviteCode) {
        await invitationService.acceptInvitation(
          inviteCode,
          userId,
          {
            email: existingUserData.email,
            firstName: existingUserData.firstName || formData.firstName,
            lastName: existingUserData.lastName || formData.lastName,
            phone: existingUserData.phone || formData.phone,
            displayName: existingUserData.displayName || `${formData.firstName} ${formData.lastName}`
          },
          invitation?.familyCode || formData.familyCode || null
        );
      }

      setExistingAccountDialogOpen(false);

      // Navigate to home
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (err) {
      logger.error('Error merging account:', err);
      setError(err.message || 'Failed to merge account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Smart Join Flow: Handle delete and continue - delete old account data and create fresh
   */
  const handleDeleteAndContinue = async (password) => {
    try {
      setLoading(true);
      setError(null);

      // Sign in first to get the user
      const userCredential = await signInWithEmailAndPassword(
        auth,
        existingUserData.email,
        password
      );

      const userId = userCredential.user.uid;

      // Delete all user data (but keep the auth account)
      await accountDeletionService.deleteUserData(userId, existingUserData.familyCode);

      // Close dialog and continue with wizard
      setExistingAccountDialogOpen(false);
      setExistingUserData(null);

      // Set the form data with the existing auth user
      setFormData(prev => ({
        ...prev,
        ssoProvider: 'existing',
        ssoUser: userCredential.user,
        email: existingUserData.email,
        userId: userId
      }));

      // Skip to family info step
      setActiveStep(2);
    } catch (err) {
      logger.error('Error deleting account data:', err);
      setError(err.message || 'Failed to delete account data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep()) {
      setError('Please fill in all required fields');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // SECURITY: Block joining without invitation
      if (activeStep === 2 && !formData.isNewFamily && !invitation && !inviteCode && !formData.familyCode) {
        setError('You must have a valid invitation to join a family.');
        setLoading(false);
        return;
      }

      // In invite-only mode, force isNewFamily to false when advancing from step 0
      if (isInviteOnlyMode() && activeStep === 0) {
        setFormData(prev => ({ ...prev, isNewFamily: false }));
      }

      // Smart Join Flow: Check for existing account on Basic Info step
      if (activeStep === 1 && !formData.ssoProvider && formData.email) {
        const emailCheck = await checkExistingEmail(formData.email);

        if (emailCheck.exists) {
          // Show dialog to handle existing account
          setExistingUserData(emailCheck.userData);
          setExistingAccountDialogOpen(true);
          setLoading(false);
          return;
        }
      }

      // All other steps: just advance to next step
      // We don't create the auth account until the final step
      setActiveStep(prev => prev + 1);
    } catch (error) {
      logger.error('Error in wizard step:', error);
      setError(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const finalizeAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      setFinalized(true); // Mark as finalized to prevent duplicate calls

      let userId;

      // Step 1: Create or use existing auth account
      if (formData.ssoProvider) {
        // SSO user already authenticated
        userId = formData.ssoUser.uid;
      } else {
        // Create email/password account
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );

        if (!userCredential?.user) {
          throw new Error('Failed to create user account');
        }

        userId = userCredential.user.uid;

        // Update auth profile
        await updateProfile(userCredential.user, {
          displayName: `${formData.firstName} ${formData.lastName}`
        });
      }

      // Step 2: Create top-level user document FIRST (required for joinFamily)
      const userRef = doc(db, 'users', userId);
      const basicUserData = {
        uid: userId,
        email: formData.email,
        displayName: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || null,
        currentFamily: null, // Will be set when joining/creating family
        familyCode: null, // Will be set when joining/creating family
        role: 'member', // Default role, upgraded to 'owner' by createFamily
        groups: [], // Default empty, populated by createFamily/joinFamily
        isAdmin: false, // Default false, set true for owners by createFamily
        accountType: formData.isNewFamily ? 'owner' : 'member',
        profileComplete: true,
        termsAccepted: true,
        termsAcceptedAt: new Date(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await setDoc(userRef, basicUserData);

      // Initialize 14-day trial for all new users (owners and invited members alike).
      // Each user has their own subscription record — members are not covered by
      // the owner's subscription in the current data model.
      try {
        await subscriptionService.initializeFreeSubscription(userId);
      } catch (subErr) {
        logger.error('Failed to initialize trial subscription:', subErr);
        // Non-fatal — AuthContext will retry auto-init on next auth state change
      }


      // Step 3: Upload profile picture if provided
      if (formData.profilePicture) {
        try {
          const storagePath = `users/${userId}/profile-pictures/${formData.profilePicture.name}`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, formData.profilePicture);
        } catch (uploadError) {
          logger.error('Profile picture upload failed:', uploadError);
          // Don't fail the whole process if photo upload fails
        }
      }

      // Step 4: Handle invitation acceptance
      if (invitation || inviteCode) {
        // Accept invitation (creates nested member with auto-assigned groups)
        const result = await invitationService.acceptInvitation(
          inviteCode,
          userId,
          {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            displayName: `${formData.firstName} ${formData.lastName}`
          },
          invitation?.familyCode || formData.familyCode || null
        );

        setFormData(prev => ({
          ...prev,
          familyCode: result.familyCode
        }));
      }
      // Step 5: Create new family
      else if (formData.isNewFamily) {
        // Create new family (creates nested structure automatically)
        const generatedFamilyCode = await familyCodeService.createFamily(
          userId,
          formData.familyName
        );

        setFormData(prev => ({ ...prev, familyCode: generatedFamilyCode }));
      }
      // SECURITY: Block joining without invitation
      else {
        throw new Error('You must have a valid invitation to join a family. Manual family code entry is disabled for security.');
      }

      // Step 6: Refresh AuthContext so currentUser has the new familyCode,
      // role, groups, and isAdmin fields.  Without this the stale auth data
      // causes permission failures and the Admin portal redirect.
      // Use waitForField to retry until Firestore replicates the familyCode.
      await refreshUserData({ waitForField: 'familyCode' });

      // Step 7: Navigate to home/dashboard
      navigate('/');
    } catch (err) {
      logger.error('Error finalizing account:', err);
      setError(err.message || 'Failed to complete setup. Please try again.');
      setFinalized(false); // Allow explicit retry via "Try Again" button
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0: // Account Type
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Welcome to Family For Life
            </Typography>
            {isInviteOnlyMode() ? (
              // Invite-only mode: skip account type selection, auto-set to join
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  You've been invited to join a family group. Let's get you set up!
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Click <strong>Next</strong> to continue setting up your account.
                  </Typography>
                </Alert>
              </>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Choose how you'd like to get started.
                </Typography>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    What would you like to do?
                  </FormLabel>
                  <RadioGroup
                    value={formData.isNewFamily === true ? 'new' : ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      isNewFamily: e.target.value === 'new'
                    }))}
                  >
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label="Create a new family group"
                    />
                  </RadioGroup>
                </FormControl>
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Want to join an existing family?
                  </Typography>
                  <Typography variant="body2">
                    To join an existing Family For Life group, you'll need an invitation link from your family administrator.
                    Ask them to send you an invite — you'll be able to sign up directly from that link.
                  </Typography>
                </Alert>
              </>
            )}
          </Box>
        );

      case 1: // Basic Info
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {formData.ssoProvider ? 'Complete Your Profile' : 'Create Your Account'}
            </Typography>

            {invitation && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You've been invited by {invitation.inviterName} to join their family group!
              </Alert>
            )}

            {!formData.ssoProvider && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Sign up with:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<GoogleIcon />}
                      onClick={handleGoogleSSO}
                      disabled={loading}
                    >
                      Google
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleMicrosoftSSO}
                      disabled={loading}
                    >
                      Microsoft
                    </Button>
                  </Box>
                  <Divider sx={{ my: 2 }}>OR</Divider>
                </Box>

                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                />
                <TextField
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  helperText="Minimum 8 characters"
                />
              </>
            )}

            {formData.ssoProvider && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Signed in with {formData.ssoProvider === 'google' ? 'Google' : 'Microsoft'}
              </Alert>
            )}

            <TextField
              required
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              required
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Phone Number (Optional)"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              margin="normal"
            />

            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                  sx={{ pt: 0.5 }}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </Typography>
              }
            />
          </Box>
        );

      case 2: // Family Info
        return formData.isNewFamily ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Create Your Family Group
            </Typography>
            <TextField
              required
              fullWidth
              label="Family Name"
              name="familyName"
              value={formData.familyName}
              onChange={handleChange}
              margin="normal"
              helperText="This will be used to generate your unique family code (e.g., 'Smith' → 'SMIT0001')"
            />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Join Your Family
            </Typography>

            {/* SECURITY: Only allow joining via valid invitation */}
            {!invitation && !inviteCode ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  No Invitation Found
                </Typography>
                <Typography variant="body2">
                  You must have a valid invitation link to join a family.
                  Family For Life uses invitation-only access to protect family privacy and security.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Please ask your family administrator to send you an invitation link.
                </Typography>
              </Alert>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Valid Invitation
                  </Typography>
                  <Typography variant="body2">
                    Family Code: <strong>{invitation?.familyCode || formData.familyCode}</strong>
                  </Typography>
                  {invitation?.inviterName && (
                    <Typography variant="body2">
                      Invited by: <strong>{invitation.inviterName}</strong>
                    </Typography>
                  )}
                </Alert>

                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Your Relationship</InputLabel>
                  <Select
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleChange}
                    label="Your Relationship"
                  >
                    {RELATIONSHIP_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {invitation?.customMessage && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Message from {invitation.inviterName}:</Typography>
                    <Typography variant="body2">{invitation.customMessage}</Typography>
                  </Alert>
                )}
              </>
            )}
          </Box>
        );

      case 3: // Profile Picture
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Upload Profile Picture
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Add a photo so your family can recognize you. You can skip this for now.
            </Typography>
            {formData.profilePicture ? (
              <Avatar
                src={URL.createObjectURL(formData.profilePicture)}
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
              />
            ) : (
              <Avatar
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2, bgcolor: 'grey.200', color: 'grey.500' }}
              >
                <CloudUpload sx={{ fontSize: 48 }} />
              </Avatar>
            )}
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              sx={{ borderRadius: 2 }}
            >
              {formData.profilePicture ? 'Change Photo' : 'Choose Photo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setFormData(prev => ({
                      ...prev,
                      profilePicture: e.target.files[0]
                    }));
                  }
                }}
              />
            </Button>
            {formData.profilePicture && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {formData.profilePicture.name}
              </Typography>
            )}
          </Box>
        );

      case 4: // Complete
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {loading ? (
              <>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {formData.isNewFamily ? 'Creating Your Family...' : 'Joining Family...'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Setting up your account, please wait...
                </Typography>
              </>
            ) : formData.familyCode ? (
              <>
                <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  You're All Set!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formData.isNewFamily ? (
                    <>
                      Your family code is: <strong>{formData.familyCode}</strong>
                      <br />
                      Use this to invite family members from the Admin Portal.
                    </>
                  ) : (
                    <>
                      You've successfully joined family: <strong>{formData.familyCode}</strong>
                    </>
                  )}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Redirecting to your dashboard...
                </Typography>
              </>
            ) : null}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  // INVITE-ONLY GUARD: If no invitation code in URL, block registration entirely.
  if (isInviteOnlyMode() && !inviteCode) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          py: 4,
          px: 2
        }}
      >
        <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, maxWidth: 500, width: '100%', borderRadius: 3, textAlign: 'center' }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
            <FamilyRestroom sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
            Invitation Required
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" gutterBottom>
              Family For Life is currently in <strong>invite-only</strong> mode.
            </Typography>
            <Typography variant="body2">
              To create an account, you need an invitation link from an existing family administrator.
              Please contact your family member who manages the Family For Life group and ask them to send you an invite.
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => navigate('/login')} sx={{ borderRadius: 2, px: 4 }}>
            Back to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  if (loadingInvitation) {
    return (
      <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading invitation...</Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
        px: 2
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 5 },
          maxWidth: 640,
          width: '100%',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Header with branding */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 1.5
            }}
          >
            <FamilyRestroom sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, color: 'primary.main' }}
          >
            Family For Life
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Set up your account to connect with your family
          </Typography>
        </Box>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={<StepConnector />}
          sx={{
            mt: 2,
            mb: 4,
            '& .MuiStepLabel-label': {
              fontSize: '0.75rem',
              mt: 0.5
            }
          }}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {getStepContent()}

        {activeStep < STEPS.length - 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            {activeStep > 0 && !inviteCode ? (
              <Button
                onClick={handleBack}
                disabled={loading}
                variant="text"
                size="large"
              >
                Back
              </Button>
            ) : (
              <Box />
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading || checkingEmail || !validateStep()}
              size="large"
              sx={{
                px: 5,
                borderRadius: 2,
                fontWeight: 600
              }}
            >
              {loading || checkingEmail ? <CircularProgress size={24} /> : 'Next'}
            </Button>
          </Box>
        )}

        {/* Smart Join Flow: Existing Account Dialog */}
        <ExistingAccountDialog
          open={existingAccountDialogOpen}
          onClose={() => {
            setExistingAccountDialogOpen(false);
            setExistingUserData(null);
          }}
          existingUserData={existingUserData}
          invitationData={invitation}
          onMerge={handleMergeAccount}
          onDeleteAndContinue={handleDeleteAndContinue}
          loading={loading}
        />
      </Paper>
    </Box>
  );
};

export default AccountCreationWizard;
